import { useState, useCallback, useRef } from 'react';

export type AnnotationMode = 'hand' | 'move' | 'text' | 'placing' | 'polygon' | 'freehand' | 'rect';

export interface ViewerController {
    activeMode: AnnotationMode | null;
    activateMode: (mode: AnnotationMode) => void;
    deactivateMode: () => void;
    handleContainerClick: (e: React.MouseEvent) => void;
    handleContainerDoubleClick: (e: React.MouseEvent) => void;
}

export function useAnnotationMode(
    modeHandlers: Record<string, (e: React.MouseEvent) => void>,
    doubleClickHandler: (e: React.MouseEvent, activeMode: AnnotationMode | null) => void,
): ViewerController {
    const [activeMode, _setActiveMode] = useState<AnnotationMode | null>(null);
    const activeModeRef = useRef<AnnotationMode | null>(null);

    const setActiveMode = useCallback((mode: AnnotationMode | null) => {
        activeModeRef.current = mode;
        _setActiveMode(mode);
    }, []);

    const activateMode = useCallback((mode: AnnotationMode) => {
        const prev = activeModeRef.current;
        if (prev === mode) {
            setActiveMode(null);
        } else {
            setActiveMode(mode);
        }
    }, [setActiveMode]);

    const deactivateMode = useCallback(() => {
        setActiveMode(null);
    }, [setActiveMode]);

    const handleContainerClick = useCallback((e: React.MouseEvent) => {
        const current = activeModeRef.current;
        if (!current) return;
        const handler = modeHandlers[current];
        if (handler) handler(e);
    }, [modeHandlers]);

    const handleContainerDoubleClick = useCallback((e: React.MouseEvent) => {
        const current = activeModeRef.current;
        doubleClickHandler(e, current);
    }, [doubleClickHandler]);

    return {
        activeMode,
        activateMode,
        deactivateMode,
        handleContainerClick,
        handleContainerDoubleClick,
    };
}
